import React, { ReactNode, useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useModalTargetRef } from '../../helpers/ModalContext'
import { useBodyScroll } from '../../helpers/useBodyScroll'
import LocalModalContext from './LocalModalContext'
import { Header } from './Header'
import styles from './Modal.module.scss'

interface IProps {
  title: string
  staticContent?: boolean
  closeModal: () => void
  children?: ReactNode
}

export const Modal = ({
  title,
  children,
  closeModal,
  staticContent,
}: IProps) => {
  const targetRef = useModalTargetRef()
  useBodyScroll(false)

  if (!targetRef.current) return null

  return createPortal(
    <LocalModalContext.Provider value={{ closeModal }}>
      <div className={styles.ModalWrapper}>
        <div className={styles.Overlay} onClick={closeModal} />
        <div className={styles.Container}>
          <Header title={title} staticContent={staticContent}/>
          {children}
        </div>
      </div>
    </LocalModalContext.Provider>,
    targetRef.current
  )
}

export const useModal = (initialIsOpen = false) => {
  const [isOpen, setIsOpen] = useState(initialIsOpen)
  const openModal = useCallback(() => setIsOpen(true), [])
  const closeModal = useCallback(() => setIsOpen(false), [])

  const Component = useMemo(() => {
    return (props: Omit<IProps, 'closeModal'>) => {
      if (!isOpen) return null

      return <Modal closeModal={closeModal} {...props} />
    }
  }, [isOpen, closeModal])

  return {
    Modal: Component,
    openModal,
    closeModal,
  }
}
